import React, { useState, useEffect } from 'react';
import { getRecipeById, updateRecipe } from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/styles.css';
import '../styles/editRecipe.css';
import { useAuth } from '../context/authContext';

const EditRecipe = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [formData, setFormData] = useState({
        name: '',
        category: '',
        cooking_time: '',
        ingredients: '',
        prerequisites: '',
        steps: '',
        images: [] // New image files
    });
    const [existingImages, setExistingImages] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchRecipe = async () => {
            try {
                const data = await getRecipeById(id);
                setFormData({
                    name: data.name || '',
                    category: data.category || '',
                    cooking_time: data.cooking_time !== undefined ? data.cooking_time : '',
                    ingredients: Array.isArray(data.ingredients) ? data.ingredients.join('\n') : '',
                    prerequisites: data.instructions && data.instructions.prerequisites 
                        ? data.instructions.prerequisites.join('\n') 
                        : '',
                    steps: data.instructions && data.instructions.steps 
                        ? data.instructions.steps.join('\n') 
                        : '',
                    images: [] // New images will be added here.
                });
                setExistingImages(data.images || []);
            } catch (err) {
                console.error('Error fetching recipe:', err);
                setError('Failed to load recipe. Please try again.');
            }
        };

        fetchRecipe();
    }, [id]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'images') {
            setFormData((prev) => ({ ...prev, images: files }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleRemoveExistingImage = (imgUrl) => {
        setExistingImages((prev) => prev.filter((img) => img !== imgUrl));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const {
            name = '',
            category = '',
            cooking_time = '',
            ingredients = '',
            prerequisites = '',
            steps = ''
        } = formData;

        if (
            !name.trim() ||
            !category.trim() ||
            cooking_time === '' ||
            !ingredients.trim() ||
            !prerequisites.trim() ||
            !steps.trim()
        ) {
            setError('All fields are required. Please fill out every section.');
            return;
        }

        try {
            if (!user) throw new Error('User not authenticated');
            const token = await user.getIdToken();

            const submitData = new FormData();
            submitData.append('name', name.trim());
            submitData.append('category', category.trim());
            submitData.append('cooking_time', cooking_time);

            // Append ingredients as an array based on newline splitting
            submitData.append(
                'ingredients',
                JSON.stringify(ingredients.split('\n').filter((i) => i.trim()))
            );

            // Combine prerequisites and steps into a single instructions object
            const instructions = {
                prerequisites: prerequisites
                    .trim()
                    .split('\n')
                    .filter((line) => line.trim()),
                steps: steps
                    .trim()
                    .split('\n')
                    .filter((line) => line.trim())
            };
            submitData.append('instructions', JSON.stringify(instructions));

            submitData.append('existingImages', JSON.stringify(existingImages));

            Array.from(formData.images).forEach((image) => {
                submitData.append('images', image);
            });

            await updateRecipe(id, submitData, token);
            navigate(`/recipe/${id}`);
        } catch (err) {
            console.error('Error updating recipe:', err);
            setError('Failed to update recipe. Please try again.');
        }
    };

    return (
        <div className="edit-recipe-container">
            <h1>Edit Recipe</h1>
            <form onSubmit={handleSubmit} className="edit-recipe-form">
                <div className="edit-form-group">
                    <label htmlFor="name" className="edit-label">
                        Name:
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="edit-input"
                        required
                    />
                </div>
                <div className="edit-form-group">
                    <label htmlFor="category" className="edit-label">
                        Category:
                    </label>
                    <input
                        type="text"
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="edit-input"
                        required
                    />
                </div>
                <div className="edit-form-group">
                    <label htmlFor="cooking_time" className="edit-label">
                        Cooking Time (minutes):
                    </label>
                    <input
                        type="number"
                        id="cooking_time"
                        name="cooking_time"
                        value={formData.cooking_time}
                        onChange={handleChange}
                        className="edit-input"
                        required
                    />
                </div>
                <div className="edit-form-group">
                    <label htmlFor="ingredients" className="edit-label">
                        Ingredients (one per line):
                    </label>
                    <textarea
                        id="ingredients"
                        name="ingredients"
                        value={formData.ingredients}
                        onChange={handleChange}
                        rows="8"
                        className="edit-textarea"
                        required
                    />
                </div>
                <div className="edit-form-group">
                    <label htmlFor="prerequisites" className="edit-label">
                        Prerequisites (one per line):
                    </label>
                    <textarea
                        id="prerequisites"
                        name="prerequisites"
                        value={formData.prerequisites}
                        onChange={handleChange}
                        rows="5"
                        className="edit-textarea"
                        required
                    />
                </div>
                <div className="edit-form-group">
                    <label htmlFor="steps" className="edit-label">
                        Steps (one per line):
                    </label>
                    <textarea
                        id="steps"
                        name="steps"
                        value={formData.steps}
                        onChange={handleChange}
                        rows="8"
                        className="edit-textarea"
                        required
                    />
                </div>
                <div className="edit-form-group">
                    <label htmlFor="images" className="edit-label">
                        Upload Images:
                    </label>
                    <input
                        type="file"
                        id="images"
                        name="images"
                        accept="image/*"
                        multiple
                        onChange={handleChange}
                        className="edit-input"
                    />
                </div>
                {existingImages.length > 0 && (
                    <div className="existing-images edit-form-group">
                        <h3>Existing Images</h3>
                        <div className="images-gallery">
                            {existingImages.map((imgUrl, index) => (
                                <div key={index} className="image-preview-container">
                                    <img
                                        src={imgUrl}
                                        alt={`Existing ${index}`}
                                        className="image-preview"
                                    />
                                    <button
                                        type="button"
                                        className="remove-image-button"
                                        onClick={() => handleRemoveExistingImage(imgUrl)}
                                    >
                                        X
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <button type="submit" className="edit-submit-button">
                    Update Recipe
                </button>
            </form>
            {error && <p className="error-message">{error}</p>}
        </div>
    );
};

export default EditRecipe;